import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Grid, Link, SvgIcon, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FONT_WEIGHT_NORMAL } from 'popTheme';
import { ReactComponent as ACHRILogo } from './achri_logo.svg';
import { ReactComponent as CIHRLogo } from './cihr_logo.svg';
import { ReactComponent as SickKidsLogo } from './sick_kids_logo.svg';
import { ReactComponent as TOHLogo } from './toh_logo.svg';
import { ReactComponent as UofCLogo } from './u_of_c_logo.svg';
import { ReactComponent as CPNLogo } from './cpn_logo.svg';
import { sendClientAnalytics } from 'redux/sagas/clientAnalyticsSaga';
import { AuditEventAction, createClientAnalytics } from '../../ClientAnalytics';
import packageInfo from '../../../package.json';

const PartnerIcon = ({ icon }) => {
  return (
    <SvgIcon
      component={icon}
      inheritViewBox
      sx={{
        height: { xs: '2.5rem', sm: '4.25rem' },
        width: 'auto',
      }}
    />
  );
};

const CollaboratorsBlurb = ({ text }) => {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
        textAlign: 'center',
        fontWeight: FONT_WEIGHT_NORMAL,
        fontSize: { sm: '1.25rem' },
        mt: { xs: '1.5rem', sm: '2.5rem' },
        lineHeight: { xs: '1.25rem', sm: '1.875rem' },
      }}
    >
      {text}
    </Typography>
  );
};

export const Footer = () => {
  const isLargeScreen = useMediaQuery('(min-width: 900px)');
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();

  theme.typography.body1 = {
    [theme.breakpoints.up('xs')]: {
      fontSize: '0.625rem',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.8125rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  };

  const messages = defineMessages({
    landingCollaboratorsKidsHelpPhone: {
      id: 'landing.collaborators.kids_help_phone',
      defaultMessage: 'Kids Help Phone: 1-800-668-6868',
      description: 'Kids help phone info on landing page',
    },
    landingCollaboratorsHopeForWellness: {
      id: 'landing.collaborators.hope_for_wellness',
      defaultMessage: 'Hope For Wellness',
      description: 'Hope For Wellness info on landing page',
    },
    landingCollaboratorsTermsOfUse: {
      id: 'landing.collaborators.terms_of_use',
      defaultMessage: 'Terms of Use',
      description: 'Terms of use link title on landing page',
    },
    landingCollaboratorsCopyrightDate: {
      id: 'landing.collaborators.copyright_date',
      defaultMessage: `POP v${packageInfo?.version?.slice(0, 5)} © ${new Date().getFullYear()}`,
      description: 'Copyright date on landing page',
    },
  });

  const StyledLink = ({ children, href, auditEvent }) => {
    return (
      <Link
        variant="body1"
        rel="noopener noreferrer"
        href={href}
        underline="hover"
        target="_blank"
        sx={{ color: 'text.secondary' }}
        onClick={() =>
          auditEvent &&
          dispatch(
            sendClientAnalytics(createClientAnalytics(auditEvent.action, auditEvent.category)),
          )
        }
      >
        {children}
      </Link>
    );
  };

  return (
    <Grid
      container
      spacing={3}
      justifyContent="space-evenly"
      py={'1.5rem'}
      sx={{
        textAlign: { xs: 'left', sm: 'center' },
        px: { xs: '1.5rem', sm: '3.75rem' },
      }}
    >
      <Grid item {...(isLargeScreen ? null : { xs: 7, sm: 4 })}>
        <StyledLink href="https://kidshelpphone.ca/" auditEvent={AuditEventAction.KHP_FOOTER}>
          {intl.formatMessage(messages.landingCollaboratorsKidsHelpPhone)}
        </StyledLink>
      </Grid>
      <Grid
        item
        {...(isLargeScreen ? null : { xs: 5, sm: 4 })}
        sx={{ [theme.breakpoints.down('sm')]: { textAlign: 'right' } }}
      >
        <StyledLink href="https://www.hopeforwellness.ca" auditEvent={AuditEventAction.HFW_FOOTER}>
          {intl.formatMessage(messages.landingCollaboratorsHopeForWellness)}
        </StyledLink>
      </Grid>
      <Grid
        item
        {...(isLargeScreen ? null : { xs: 6, sm: 3 })}
        sx={{ [theme.breakpoints.down('sm')]: { textAlign: 'right' } }}
      >
        <StyledLink
          href="https://www.popyouth.ca/terms-conditions"
          auditEvent={AuditEventAction.TC_FOOTER}
        >
          {intl.formatMessage(messages.landingCollaboratorsTermsOfUse)}
        </StyledLink>
      </Grid>
      <Grid
        item
        {...(isLargeScreen ? null : { xs: 6, sm: 3 })}
        sx={{ [theme.breakpoints.down('sm')]: { textAlign: 'center' } }}
      >
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {intl.formatMessage(messages.landingCollaboratorsCopyrightDate)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const Collaborators = () => {
  const theme = useTheme();
  const intl = useIntl();

  theme.typography.h1 = {
    [theme.breakpoints.only('xs')]: {
      fontSize: '1.25rem',
    },
  };
  theme.typography.body2 = {
    [theme.breakpoints.only('xs')]: {
      fontSize: '0.8125rem',
    },
  };

  const messages = defineMessages({
    landingCollaboratorsTitle: {
      id: 'landing.collaborators.title',
      defaultMessage: 'Brought To You By',
      description: 'Title for collaborators section on landing page.',
    },
    landingCollaboratorsBlurb: {
      id: 'landing.collaborators.blurb',
      defaultMessage:
        "In collaboration with: Pain BC, Chronic Pain Network Strategy for Patient-Oriented Research, Solutions for Kids in Pain, Cassie & Friends, Caring for Kids, The ILC Foundation, Rare Disease Foundation, The Ottawa Hospital, and Health Canada's Substance Use and Addictions Program.",
      description: 'Blurb for collaborators section on landing page.',
    },
  });

  return (
    <Grid container direction="column" wrap="nowrap" rowSpacing={{ xs: '1.5rem', sm: '4.75rem' }}>
      <Grid
        item
        xs={9}
        sx={{
          [theme.breakpoints.up('sm')]: {
            width: '50%',
          },
          alignSelf: 'center',
        }}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          py={{ xs: '4.5rem', sm: '1.875rem' }}
          rowSpacing={{ xs: '1.5rem', sm: '4.75rem' }}
        >
          <Grid item xs={4}>
            <Typography
              variant="h1"
              sx={{
                color: 'text.secondary',
              }}
            >
              {intl.formatMessage(messages.landingCollaboratorsTitle)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Grid
              container
              display="flex"
              flexDirection="row"
              justifyItems="center"
              width="100%"
              columnSpacing={{ xs: '0rem' }}
              rowSpacing={{ xs: '1.5rem', sm: '2.75rem' }}
              textAlign="center"
            >
              <Grid item xs={6}>
                <PartnerIcon icon={SickKidsLogo} />
              </Grid>
              <Grid item xs={6}>
                <PartnerIcon icon={ACHRILogo} />
              </Grid>
              <Grid item xs={6}>
                <PartnerIcon icon={UofCLogo} />
              </Grid>
              <Grid item xs={6}>
                <PartnerIcon icon={CIHRLogo} />
              </Grid>
              <Grid item xs={12}>
                <PartnerIcon icon={TOHLogo} />
              </Grid>
              <Grid item xs={12}>
                <PartnerIcon icon={CPNLogo} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4} px={{ xs: '3.125rem', sm: '5.625rem' }}>
            <CollaboratorsBlurb text={intl.formatMessage(messages.landingCollaboratorsBlurb)} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={3} justifyContent="space-between">
        <Footer />
      </Grid>
    </Grid>
  );
};
